import type { WorkflowJSONSchema, WorkflowNodeType } from "@/types/workflow.types";
import {
  humanizeConfigKey,
  isJsonEditorKey,
  isJsonLike,
  isTextEditorKey,
  parseStructuredValue,
} from "./structured-config-utils";

export interface CatalogFieldDef {
  key: string;
  label: string;
  schema: WorkflowJSONSchema;
  required: boolean;
}

export function buildCatalogFields(
  nodeType: WorkflowNodeType | undefined,
  config: Record<string, unknown>,
): CatalogFieldDef[] {
  const properties = nodeType?.configSchema.properties ?? {};
  const required = new Set(nodeType?.configSchema.required ?? []);
  const order = nodeType?.configUiSchema.order ?? [];
  const propertyNormalizedKeys = new Set(Object.keys(properties).map(normalizeConfigKey));
  const keys = [
    ...order.filter((key) => properties[key]),
    ...Object.keys(properties)
      .filter((key) => !order.includes(key))
      .sort(),
    ...Object.keys(config)
      .filter((key) => !properties[key] && !propertyNormalizedKeys.has(normalizeConfigKey(key)))
      .sort(),
  ];
  return keys.map((key) => ({
    key,
    label: humanizeConfigKey(key),
    schema: schemaWithKnownOptions(
      key,
      properties[key] ?? inferSchema(catalogConfigValueForKey(key, config)),
    ),
    required: required.has(key),
  }));
}

export function catalogConfigValueForKey(key: string, config: Record<string, unknown>): unknown {
  if (Object.prototype.hasOwnProperty.call(config, key)) return config[key];
  const normalized = normalizeConfigKey(key);
  const alias = Object.keys(config).find(
    (configKey) => normalizeConfigKey(configKey) === normalized,
  );
  return alias ? config[alias] : undefined;
}

export function initialCatalogValue(value: unknown, schema: WorkflowJSONSchema): unknown {
  if (value !== undefined) return value;
  if (schema.default !== undefined) return schema.default;
  if (schema.type === "array") return [];
  if (schema.type === "object") return {};
  if (schema.type === "boolean") return false;
  return "";
}

export function initialCatalogValueForKey(
  key: string,
  value: unknown,
  schema: WorkflowJSONSchema,
): unknown {
  if (value !== undefined || schema.default !== undefined)
    return initialCatalogValue(value, schema);
  const fallback = DEFAULT_VALUES_BY_KEY[normalizeConfigKey(key)];
  return fallback !== undefined ? fallback : initialCatalogValue(value, schema);
}

export function defaultCatalogValueForKey(key: string, schema: WorkflowJSONSchema): unknown {
  if (schema.default !== undefined) return schema.default;
  return DEFAULT_VALUES_BY_KEY[normalizeConfigKey(key)];
}

export function parseCatalogFieldValue(
  key: string,
  schema: WorkflowJSONSchema,
  value: unknown,
  draft: string,
): unknown {
  if (
    schema.type === "array" ||
    schema.type === "object" ||
    isJsonLike(value) ||
    isJsonEditorKey(key.toLowerCase())
  ) {
    return parseStructuredValue(key, value, draft);
  }
  if (schema.type === "string" && isTextEditorKey(key.toLowerCase())) return draft;
  if (schema.type === "number" || schema.type === "integer")
    return value === "" ? undefined : value;
  return value;
}

export function inferSchema(value: unknown): WorkflowJSONSchema {
  if (Array.isArray(value)) return { type: "array" };
  if (typeof value === "boolean") return { type: "boolean" };
  if (typeof value === "number") return { type: "number" };
  if (isJsonLike(value)) return { type: "object" };
  return { type: "string" };
}

export function isEmptyOptionalCatalogValue(value: unknown) {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return typeof value === "object" && value !== null && Object.keys(value).length === 0;
}

function normalizeConfigKey(key: string) {
  return key.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
}

const DEFAULT_VALUES_BY_KEY: Record<string, unknown> = {
  timeout: 0,
  max_rows: 100000,
  output_as: "single",
};

const KNOWN_ENUMS_BY_KEY: Record<string, string[]> = {
  output_as: ["single", "multiple"],
};

function schemaWithKnownOptions(key: string, schema: WorkflowJSONSchema): WorkflowJSONSchema {
  if (schema.enum?.length) return schema;
  const enumValues = KNOWN_ENUMS_BY_KEY[normalizeConfigKey(key)];
  return enumValues ? { ...schema, type: "string", enum: enumValues } : schema;
}
